/* eslint-disable max-len */
import { useEffect } from 'react';
import proptypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { copy } from '@thd-olt-functional/utils';
import {
  formatProductLabel,
  getImagePaths,
  getProductSchemas,
  getProductType,
  getBrandName,
  generateProductTitle,
  generateProductDescription
} from '../../helpers/product-helpers';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import { emtContentModel, productModel, reviewsModel } from '../../models';
import {
  ANDROID_HREF,
  HD_URL,
  OPEN_GRAPH_FACEBOOK_ADMIN
} from '../../constants';
import {
  generateTags,
  isCanonicalUrlValid,
  isTitleValid
} from '../../utils';
import {
  getProductStructuredData,
  getProductGroupSchema,
  getBreadcrumbStructuredData,
  getVideoStructuredData
} from '../../structured-data';

const { bool, string } = proptypes;

const productMetaDataHandler = ({ data, props = {} }) => {
  const { reviews: reviewsData, product: productData } = data || {};

  if (!productData) {
    return [];
  }
  const { productGroup } = data?.emtContent?.content || {};
  const {
    itemId,
    details,
    identifiers = {},
    seo,
    availabilityType,
    media = {},
    taxonomy,
    pricing,
    specificationGroup,
    info
  } = productData || {};

  // ---- Flags & Labels ----

  const returnable = info?.returnable;
  const brandName = getBrandName({ identifiers });
  const productLabel = formatProductLabel(
    brandName,
    identifiers?.productLabel,
    identifiers?.modelNumber
  );
  const isPPQ = props?.ppq;
  const { video, image } = media;
  const { pageNumber, pageDescription } = props;

  // ---- Title, Description, & Type ----

  const fallbackTitle = generateProductTitle({
    productLabel,
    pageNumber: props?.pageNumber,
    isPPQ
  });

  const productDescription = details?.description;
  const seoDescription = seo?.seoDescription;
  const fallbackDescription = generateProductDescription({
    pageDescription,
    seoDescription,
    productDescription,
    pageNumber,
    isPPQ
  });

  const fallbackType = getProductType(isPPQ);

  // ---- Other Tags ----
  const { canonicalUrl: canonicalPath = '' } = identifiers;
  const canonical = `${HD_URL}${canonicalPath}`;
  const isCanonicalValid = isCanonicalUrlValid(canonical);

  const primaryImage = image?.url;
  const preloadImages = getImagePaths({
    primaryImage,
    mediaImages: media?.images,
    includeSecondary: true
  });
  const alternate = `${ANDROID_HREF}product/${itemId}`;
  const isNoFollow = availabilityType?.discontinued;

  const emtContentMetadata = data?.emtContent?.content?.metadata || {};
  const {
    metaDescription = fallbackDescription,
    ogImage = primaryImage,
    ogType = fallbackType,
    schema,
    title = fallbackTitle,
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const isTitlePageValid = isTitleValid(title);

  // ---- Schema Tag ----
  const productStructure = getProductStructuredData({
    reviewsData,
    identifiers,
    productLabel,
    media,
    productDescription,
    pricing,
    specifications: specificationGroup,
    availabilityType,
    schemaExclude: props.schema,
    ppq: props.ppq,
    returnable,
    hideIfHDPPSku: props.hideIfHDPPSku
  });

  const breadcrumbStructuredData = getBreadcrumbStructuredData({
    canonical,
    breadcrumbs: copy(taxonomy?.breadCrumbs || []).data,
    title,
    brandName,
    brandLinkUrl: taxonomy?.brandLinkUrl
  });

  const videoStructuredData = video?.length
    ? getVideoStructuredData({ videos: video, productLabel })
    : {};

  const productGroupSchema = productGroup?.productGroupName
    ? getProductGroupSchema({
      product: productData,
      reviews: reviewsData,
      groupName: productGroup.productGroupName
    })
    : {};

  const schemas = getProductSchemas({
    schema,
    schemaId: 'productStructureData',
    fallbackSchemas: [
      { content: productStructure, id: 'productStructureData' },
      { content: breadcrumbStructuredData, id: 'breadcrumbStructureData' },
      { content: videoStructuredData, id: 'videoStructureData' },
      { content: productGroupSchema, id: 'productGroupSchema' }
    ],
  });

  return generateTags({
    ...(isTitlePageValid && {
      title,
      ogTitle: title,
    }),
    description: metaDescription,
    ogDescription: metaDescription,
    ...(isCanonicalValid && {
      canonical,
      ogUrl: canonical,
    }),
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    alternate,
    ogImage,
    ogType,
    preloadImages,
    ogSiteName: 'The Home Depot',
    robots: isNoFollow,
    schemas
  });
};

const ProductMetadata = ({
  itemId,
  pageDescription = null,
  pageNumber = null,
  pageTitle = null,
  ppq = false,
  questionsCanonical = false,
  reviewsCanonical = false,
  hideIfHDPPSku = false
}) => {
  const pageSize = 30;
  const storeId = useStoreId();
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-metadata.ready');
  }, []);
  const productOptions = {
    variables: {
      itemId,
      storeId
    }
  };

  let startIndex = (pageNumber - 1) * pageSize;
  startIndex = pageNumber > 1 ? startIndex + 1 : startIndex;

  const reviewsOptions = {
    variables: {
      itemId,
      ...(pageNumber && {
        startIndex,
        pagesize: pageSize,
        recfirstpage: pageSize
      })
    }
  };

  const emtContentOptions = {
    variables: {
      contentId: itemId,
      pageName: 'pip'
    },
    skip: !itemId
  };

  const { data, loading, error, variables } = useDataModel('product', productOptions);
  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('reviews', reviewsOptions);
  const { data: emtData, loading: emtLoading, error: emtError } = useDataModel('emtContent', emtContentOptions);

  const props = {
    reviewsCanonical,
    pageDescription,
    pageNumber,
    pageTitle,
    questionsCanonical,
    ppq,
    hideIfHDPPSku
  };

  const helmetData = {
    product: data?.product,
    reviews: !(props?.hideIfHDPPSku && data?.product?.identifiers?.skuClassification === 'hdpp') && reviewData?.reviews,
    emtContent: emtData?.emtContent,
  };

  const deps = data?.product && reviewData?.reviews && data?.product?.itemId;

  useHelmet('productMetadata', { data: helmetData, props, variables, skipCleanup: true }, productMetaDataHandler, [deps]);
  return null;
};

ProductMetadata.displayName = 'ProductMetadata';

ProductMetadata.dataModel = extend(productModel, reviewsModel, emtContentModel);

ProductMetadata.propTypes = {
  itemId: string.isRequired,
  reviewsCanonical: bool,
  pageNumber: string,
  pageTitle: string,
  pageDescription: string,
  questionsCanonical: bool,
  ppq: bool,
  hideIfHDPPSku: bool
};

export { ProductMetadata };
